import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { TranslocoDirective } from '@jsverse/transloco'
import { ModalComponent } from './modal/modal.component'
import { ModalService } from '@/services/modal.service'
import { SiteConfigService } from '@/services/site-config.service'
import { injectLogger } from '@/services/logger.service'
import { BaseButtonComponent } from '../base/base-button/base-button.component'
import { BaseInputComponent } from '../base/base-input/base-input.component'
import { RegisterPromptComponent } from '../other/authentication/register-prompt/register-prompt.component'

/** Like the other modals' data: a caller can replace the title and the line under it. */
export interface ResetPasswordModalData {
	title: string;
	body: string;
}

/** Opened from Forgot password, in the header's inline form and in the login modal. */
@Component({
	selector: 'reset-password-modal',
	imports: [
		ModalComponent, TranslocoDirective, ReactiveFormsModule, BaseButtonComponent, BaseInputComponent, RegisterPromptComponent
	],
	styles: `
		/* The theme gives headings their own line-height: back to the font's. */
		h2 {
			width: fit-content;
			margin: 0 auto;
			padding: 2px 0 10px;
			font-size: var(--font-size-xl);
			font-weight: var(--font-weight-semibold);
			line-height: normal;
		}

		.reset-prompt {
			max-width: 345px;
			margin: 0 auto 1.5rem;
			text-align: center;
			font-size: var(--font-size-normal);
			font-weight: var(--font-weight-medium);
			color: var(--modal-light-text-muted);
		}

		/* 345px of content in the design's 24px-padded modal. */
		form {
			display: flex;
			flex-direction: column;
			width: 345px;
			max-width: 100%;
		}

		base-input {
			height: 42px;
			--input-border: 1px solid var(--modal-light-border);
			--input-padding: 8px 8px 8px 12px;
		}

		/* The call to action: a full-width 48px green button, 24px under the field. */
		.send-btn {
			height: 48px;
			margin-top: 1.5rem;
			justify-content: center;
			align-items: center;
			--button-font-size: var(--font-size-normal);
			--button-font-weight: var(--font-weight-medium);
		}

		.register-prompt {
			margin-top: 0.75rem;
		}
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<app-modal *transloco="let t" surface="light" (closed)="modalService.close('resetPassword')">
			<div modal-title>
				<h2>{{ data()?.title || t('message.info.resetYourPassword') }}</h2>
			</div>
			<div modal-body>
				<p class="reset-prompt">{{ data()?.body || t('message.info.enterTheEmailYouRegisteredWith') }}</p>
				<form [formGroup]="form" (ngSubmit)="sendLink()">
					<base-input
						type="email"
						id="input-reset-email"
						formControlName="email"
						autocomplete="email"
						[placeholder]="t('user.email')"
					/>

					<base-button class="send-btn" variant="success" type="submit" [block]="true">
						{{ t('actions.sendLink') }}
					</base-button>

					@if (siteConfigService.config.allowPlayerRegistrationOnSite) {
						<register-prompt class="register-prompt" (navigated)="modalService.close('resetPassword')" />
					}
				</form>
			</div>
		</app-modal>
	`
})
export class ResetPasswordModalComponent {
	protected readonly modalService = inject(ModalService)
	protected readonly siteConfigService = inject(SiteConfigService)
	private readonly logger = injectLogger('ResetPasswordModalComponent')

	protected readonly data = this.modalService.getData('resetPassword')

	protected readonly form = new FormGroup({
		email: new FormControl('', [Validators.required, Validators.email])
	})

	// TODO: Send the reset link once the player API has an endpoint for it.
	protected sendLink(): void {
		if (this.form.invalid) return

		this.logger.warn('not implemented: Send password reset link')
	}
}
